import { useState } from "react";
import { Loader2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  ENRICHMENT_BATCH_PRESETS,
  ENRICHMENT_DEFAULT_BATCH_SIZE,
} from "@/api/enrichment";

interface EnrichmentSessionSetupProps {
  title: string;
  description: string;
  isOpening: boolean;
  /** Server message from a failed open. Rendered verbatim: the backend
   *  writes it for the operator. A 409 means the project has no "Wrong
   *  Owner" tag; a 400 means the lot went over the project's ceiling and
   *  names that ceiling — which is why we don't carry a copy of it here. */
  error: string | null;
  onStart: (count: number) => void;
  /** Rows this queue can hand out right now, or null while the counter
   *  is still loading (or its read failed). Zero is a legitimate answer,
   *  not a fault: a project that never flagged a "Wrong Owner" has an
   *  empty corrections queue, and the loud refusal for that belongs on
   *  the session start, not on a badge. */
  queueCount: number | null;
  /** Accent for the batch-size selection, matching each queue's colour
   *  elsewhere on the page. */
  accent: "indigo" | "amber";
}

const ACCENTS = {
  indigo: {
    selected: "border-indigo-500 bg-indigo-500 text-white",
    idle: "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
    count: "text-indigo-600",
  },
  amber: {
    selected: "border-amber-500 bg-amber-500 text-white",
    idle: "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
    count: "text-amber-600",
  },
} as const;

/** Entry point for both enrichment queues. The operator picks how many rows
 *  to reserve; the backend hands back that many (or fewer), hides them from
 *  other operators, and holds them for a window sized to the lot.
 *
 *  The size field takes any integer — the presets are shortcuts, not the
 *  allowed values. The maximum is a per-project setting, so it is asked
 *  for rather than assumed: an over-sized request goes out and comes back
 *  as a 400 naming the project's real limit. */
export function EnrichmentSessionSetup({
  title,
  description,
  isOpening,
  error,
  onStart,
  queueCount,
  accent,
}: EnrichmentSessionSetupProps) {
  // One source of truth for the size: the presets write into the same
  // field the operator can type in, so the two can never disagree about
  // what the Start button is about to send.
  const [raw, setRaw] = useState(String(ENRICHMENT_DEFAULT_BATCH_SIZE));
  const styles = ACCENTS[accent];

  const parsed = Number.parseInt(raw.trim(), 10);
  // The only bound worth checking here is the schema's own `>= 1`. The
  // upper one is the project's, and duplicating it is what this whole
  // change is undoing.
  const count = Number.isInteger(parsed) && parsed >= 1 ? parsed : null;

  return (
    <div className="mx-auto max-w-lg space-y-6 rounded-plane bg-white p-6 shadow-plane">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <p className="text-sm text-slate-500">{description}</p>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-slate-700">
          How many to reserve?
        </label>
        <div className="flex flex-wrap items-center gap-2">
          {ENRICHMENT_BATCH_PRESETS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRaw(String(n))}
              className={cn(
                "rounded-lg border px-3.5 py-2 text-sm font-medium tabular-nums transition-all",
                count === n ? styles.selected : styles.idle,
              )}
            >
              {n}
            </button>
          ))}
          <Input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            aria-label="Batch size"
            className="h-[38px] w-24 tabular-nums"
          />
        </div>
        <p className="mt-2 text-xs text-slate-400">
          A smaller batch than you asked for just means the queue is
          shorter, not that something went wrong.
        </p>
        <p className="mt-1 text-xs text-slate-400">
          The reservation is sized to the lot, so a bigger batch is held for
          longer — you&rsquo;ll see the exact window once it starts. A very
          large one can outlast its hold: rows you haven&rsquo;t reached by
          then go back to the queue, while everything you save or skip is
          released as you go.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {/* Same number the session will report back as `queue_remaining`:
          * eligible rows minus every live reservation, this operator's
          * own included. Which is why it drops by the size of the lot the
          * moment a session opens — those rows are in a batch now. */}
        <div className="text-sm text-slate-500">
          {queueCount === null ? (
            <span className="text-slate-400">Counting the queue&hellip;</span>
          ) : queueCount === 0 ? (
            <span>Nothing to process right now.</span>
          ) : (
            <>
              <span
                className={cn("font-semibold tabular-nums", styles.count)}
              >
                {queueCount}
              </span>{" "}
              to process
              <span className="ml-1 text-xs text-slate-400">
                (what nobody is holding)
              </span>
            </>
          )}
        </div>

        <Button
          onClick={() => count !== null && onStart(count)}
          size="lg"
          className="w-full"
          disabled={isOpening || count === null}
        >
          {isOpening ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Play className="mr-2 h-4 w-4" />
          )}
          {count === null ? "Start session" : `Start session (${count})`}
        </Button>
      </div>
    </div>
  );
}
